/**
 * Canonical lead-status colour palette used across the analytics-reports
 * surface (popups, tables, chips, KPI cards, funnel bars). Kept in one
 * file so updates propagate — historically this was duplicated in
 * TreatmentLeadPopup and SourceLeadPopup and the two drifted.
 *
 * `STATUS_COLORS`  — chip palette for lead-status tags in popup tables.
 *                   NEW intentionally stays red (Not Booked = needs
 *                   attention) rather than the canonical cyan.
 * `STATUS_CONFIG`  — popup header accent (solid colour + icon) per stage.
 * `DATE_LABELS`    — column label used for the status-specific date
 *                   (booked, attended, started, etc.).
 */

export const STATUS_COLORS = {
    NEW:                            { bg: '#fee2e2',   text: '#dc2626', label: 'Not Booked' },
    CONSULTATION_BOOKED:            { bg: '#8E33FF29', text: '#5119B7', label: 'Consultation Booked' },
    CONSULTATION_ATTENDED:          { bg: '#FFC10729', text: '#B78103', label: 'Consultation Attended' },
    TREATMENT_STARTED:              { bg: '#54D62C29', text: '#229A16', label: 'Treatment Started' },
    FAILED_TO_ATTEND_CONSULTATION:  { bg: '#FFE5D0',   text: '#C2410C', label: 'Failed to Attend' },
    CLOSED:                         { bg: '#919EAB29', text: '#637381', label: 'Completed Treatment' },
    // ARCHIVED was missing here while STATUS_CONFIG carried it, so the Lost
    // Leads drilldown labelled the status correctly in its HEADER chip and
    // leaked the raw enum ("ARCHIVED") in its Status COLUMN, one line apart.
    // Red matches STATUS_CONFIG.ARCHIVED below. Parity between the two maps is
    // enforced by tests/unit/reports/reportStatusPaletteGaps.test.js.
    ARCHIVED:                       { bg: '#fee2e2',   text: '#ef4444', label: 'Lost Leads' },
};

// Popup header palette — one unique colour per lead stage, matches the
// KPI card / funnel bar colours used elsewhere on the reports pages:
//   blue   #3b82f6 → Total Leads (NEW)
//   purple #8b5cf6 → Consultations Booked
//   amber  #f59e0b → Consultations Attended
//   green  #10b981 → Treatment Started
//   grey   #64748b → Completed
//   orange #f97316 → Failed to Attend
//   red    #ef4444 → Lost Leads (ARCHIVED)
export const STATUS_CONFIG = {
    All:                          { label: 'All Leads',              color: '#3b82f6', icon: 'eva:people-fill' },
    NEW:                          { label: 'Total Leads',            color: '#3b82f6', icon: 'eva:people-fill' },
    CONSULTATION_BOOKED:          { label: 'Consultation Booked',    color: '#8b5cf6', icon: 'eva:calendar-fill' },
    CONSULTATION_ATTENDED:        { label: 'Consultation Attended',  color: '#f59e0b', icon: 'eva:checkmark-circle-fill' },
    TREATMENT_STARTED:            { label: 'Treatment Started',      color: '#10b981', icon: 'eva:activity-fill' },
    FAILED_TO_ATTEND_CONSULTATION:{ label: 'Failed to Attend',       color: '#f97316', icon: 'eva:alert-circle-fill' },
    CLOSED:                       { label: 'Completed Treatment',    color: '#64748b', icon: 'eva:checkmark-square-fill' },
    ARCHIVED:                     { label: 'Lost Leads',             color: '#ef4444', icon: 'eva:archive-fill' },
};

export const DATE_LABELS = {
    NEW: 'Enquiry Date',
    CONSULTATION_BOOKED: 'Booked Date',
    CONSULTATION_ATTENDED: 'Attended Date',
    TREATMENT_STARTED: 'Started Date',
    CLOSED: 'Completed Date',
    FAILED_TO_ATTEND_CONSULTATION: 'FTA Date',
    ARCHIVED: 'Archived Date',
};

/**
 * Title fragment for a drilldown popup opened from a status-breakdown table.
 *
 * TreatmentLeadPopup's header already renders "<name> — <status label> (n)", so a
 * caller that folds the status into `name` gets it twice: "ONLINE_FORM — Completed
 * — Completed (2)". But the status label is NOT always redundant — the revenue
 * column opens the same popup with `{ id: 'CLOSED', label: 'Revenue' }`, and
 * dropping that would make the Revenue drilldown read identically to the
 * Completed one.
 *
 * So: keep the caller's label only when it says something the popup header won't.
 *
 * @param {string} entity      source / referral / landing URL the row represents
 * @param {string} statusLabel the label the calling table used for the clicked cell
 * @param {string} status      the status enum the cell maps to
 * @param {string} fallback    used when the row has no entity name
 */
export const buildDrilldownTitle = (entity, statusLabel, status, fallback = 'Leads') => {
    const canonical = STATUS_CONFIG[status]?.label;
    const extra = statusLabel && statusLabel !== canonical ? statusLabel : null;
    return [entity || fallback, extra].filter(Boolean).join(' — ');
};

/**
 * Heading for a lead drilldown popup: "<scope> — <status label>", minus the
 * scope when it adds nothing.
 *
 * TreatmentLeadPopup renders `<name> — <status label> (n)`, which assumed every
 * caller passes a name that scopes the status (a treatment, a source, a landing
 * URL). Overview's funnel and KPI handlers pass the STAGE label instead, which
 * already IS the status label, so live titles read "Treatment Started —
 * Treatment Started (32)" and "Lost Leads — Lost Leads (50)".
 *
 * Comparison is on a normalised token set, not string equality, because callers
 * phrase the same thing differently: "Consultations Booked" vs "Consultation
 * Booked", "Attended Consultations" vs "Consultation Attended". Tokens are
 * lowercased and de-pluralised, then compared as a set so word order does not
 * matter. A scope carrying real information ("Invisalign", "ONLINE_FORM",
 * "ONLINE_FORM — Revenue") shares no token set with the status and survives.
 *
 * Fixing it here rather than at each call site means a future caller cannot
 * reintroduce the doubling.
 */
const headingTokens = (value) => new Set(
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.replace(/s$/, ''))
);

const sameTokens = (a, b) => {
    const A = headingTokens(a);
    const B = headingTokens(b);
    if (A.size === 0 || A.size !== B.size) return false;
    return [...A].every((t) => B.has(t));
};

export const buildPopupHeading = (scope, statusLabel) => (
    scope && !sameTokens(scope, statusLabel)
        ? `${scope} — ${statusLabel}`
        : String(statusLabel || '')
);
